import { useState } from "react"
import { Box, Text, VStack, HStack, Grid, GridItem, Accordion } from "@chakra-ui/react"
import { FaBox } from "react-icons/fa6"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import StatusBadge from "@hydrologiq/shared/components/StatusBadge"
import { CollapseButton, ExpandButton } from "@hydrologiq/shared/content/components/Button"
import { ACTIVITY_KIND_CONFIGS } from "../calendar/TimelineView"
import Link from "@hydrologiq/shared/components/Link"

/**
 * Gets the customer name from an order, preferring the sites array over legacy customer field
 */
export const getOrderCustomerName = (order: gqlTypes.Order): string | undefined => {
  if (order.sites && order.sites.length > 0) {
    return order.sites[0]?.customer?.name
  }
  return undefined
}

/**
 * Gets the site name from an order, preferring the sites array over legacy site field
 */
export const getOrderSiteName = (order: gqlTypes.Order): string | undefined => {
  if (order.sites && order.sites.length > 0) {
    return order.sites[0]?.site?.name
  }
  return undefined
}

/**
 * Formats a date string to locale date format
 */
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "—"
  return new Date(dateString).toLocaleDateString()
}

/**
 * Formats a date range with null-safe handling
 */
const formatDateRange = (start: string | null | undefined, end: string | null | undefined): string => {
  const startStr = start ? new Date(start).toLocaleDateString() : "—"
  const endStr = end ? new Date(end).toLocaleDateString() : "Open-ended"
  return `${startStr} - ${endStr}`
}

/**
 * Maps OrderStatus to StatusBadge status type
 */
const mapOrderStatusToBadgeStatus = (
  status: gqlTypes.OrderStatus
): "scheduled" | "in_progress" | "completed" | "billed" => {
  switch (status) {
    case gqlTypes.OrderStatus.Scheduled:
      return "scheduled"
    case gqlTypes.OrderStatus.Started:
      return "in_progress"
    case gqlTypes.OrderStatus.Completed:
      return "completed"
    case gqlTypes.OrderStatus.Billed:
      return "billed"
    default:
      return "scheduled"
  }
}

interface OrderBannerProps {
  order: gqlTypes.Order
  /** Optional title prefix, defaults to "Fulfilling order:" */
  titlePrefix?: string
  /** Whether to show the box icon */
  showIcon?: boolean
  /** Default expanded state */
  defaultExpanded?: boolean
}

/**
 * OrderBanner - A collapsible banner that displays order context information.
 *
 * Shows a compact summary in collapsed state, and full order details when expanded.
 */
function OrderBanner({
  order,
  titlePrefix = "Fulfilling order:",
  showIcon = true,
  defaultExpanded = false,
}: OrderBannerProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const customerName = getOrderCustomerName(order)
  const siteName = getOrderSiteName(order)

  return (
    <Accordion.Root
      collapsible
      variant={"secondary"}
      defaultValue={defaultExpanded ? [order.id] : []}
      onValueChange={(value) => setIsExpanded(value.value.includes(order.id))}
    >
      <Accordion.Item value={order.id}>
        <Accordion.ItemTrigger>
          <HStack justify="space-between" align="flex-start" width="100%">
            <VStack align="flex-start" gap={1}>
              <HStack gap={2}>
                {showIcon && (
                  <Box>
                    <FaBox color={ACTIVITY_KIND_CONFIGS.fulfilment.backgroundColor} />
                  </Box>
                )}
                <Text fontWeight="semibold">
                  {titlePrefix} {order.name}
                </Text>
              </HStack>
              <Text fontSize="sm">
                {customerName && <>{customerName} • </>}
                {formatDateRange(order.start, order.end)}
                {order.quantity != null && <> • {order.quantity} kg</>}
              </Text>
            </VStack>
            <Box pt={1}>
              {isExpanded ? (
                <CollapseButton description="Collapse order details" />
              ) : (
                <ExpandButton description="Expand order details" />
              )}
            </Box>
          </HStack>
        </Accordion.ItemTrigger>
        <Accordion.ItemContent>
          <VStack fontSize="sm" align="stretch" gap={2} borderRadius="md">
            <Grid templateColumns="repeat(2, 1fr)" gap={3}>
              {customerName && (
                <GridItem>
                  <Text color="gray.500" fontWeight="medium">
                    Customer
                  </Text>
                  <Text>{customerName}</Text>
                </GridItem>
              )}

              {siteName && (
                <GridItem>
                  <Text color="gray.500" fontWeight="medium">
                    Site
                  </Text>
                  <Text>{siteName}</Text>
                </GridItem>
              )}

              <GridItem>
                <Text color="gray.500" fontWeight="medium">
                  Period
                </Text>
                <Text>
                  {formatDateRange(order.start, order.end)}
                </Text>
              </GridItem>

              <GridItem>
                <Text color="gray.500" fontWeight="medium">
                  Status
                </Text>
                <StatusBadge status={mapOrderStatusToBadgeStatus(order.status)} />
              </GridItem>

              {order.quantity != null && (
                <GridItem>
                  <Text color="gray.500" fontWeight="medium">
                    Quantity Ordered
                  </Text>
                  <Text>{order.quantity} kg</Text>
                </GridItem>
              )}

              {order.quantity_fulfilled != null && (
                <GridItem>
                  <Text color="gray.500" fontWeight="medium">
                    Quantity Planned
                  </Text>
                  <Text>{order.quantity_fulfilled} kg</Text>
                </GridItem>
              )}

              {order.quantity_consumed != null && (
                <GridItem>
                  <Text color="gray.500" fontWeight="medium">
                    Quantity Consumed
                  </Text>
                  <Text>{order.quantity_consumed} kg</Text>
                </GridItem>
              )}
            </Grid>

            <Box mt={3} textAlign="right">
              <Link href={`/orders?order=${order.id}`} variant="primary">
                View Order
              </Link>
            </Box>
          </VStack>
        </Accordion.ItemContent>
      </Accordion.Item>
    </Accordion.Root>
  )
}

export default OrderBanner
